import React from "react";
import { ME_INSIGHTS_BASE, getActivitySessions, getSessions } from "../lib/api";
import { Card, EmptyState, Pill, RowButton, Table } from "../design";
import { SessionTimeline } from "./insights/ActivityPanel";
import { humanize, shortRepo, timeOf, usdShort } from "./insights/util";
import type { AuthHeaders } from "../lib/auth";
import type { ActivitySession, Session, SessionsOut } from "../lib/types";

/** The repository as every other table names it, else the folder dense runs in. */
export function liveProject(s: Session, match?: ActivitySession): string {
  if (match?.repo) return shortRepo(match.repo);
  return s.cwd.split(/[\\/]/).filter(Boolean).pop() ?? s.cwd;
}

function elapsed(startIso: string, now: number): string {
  const mins = Math.max(0, Math.round((now - Date.parse(startIso)) / 60000));
  return mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

function isoDay(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Sessions running right now. Each one is looked up in the analytics
 * sessions by id, so its repository and spend read as in the table below. */
export function Sessions({
  authHeaders,
  zdr = false,
}: {
  authHeaders: AuthHeaders;
  zdr?: boolean;
}) {
  const [data, setData] = React.useState<SessionsOut | null>(null);
  const [open, setOpen] = React.useState<ActivitySession | null>(null);

  React.useEffect(() => {
    if (zdr) return; // ZDR: nothing is tracked, so don't poll.
    let cancelled = false;
    let timer: number | null = null;

    async function tick() {
      try {
        const d = await getSessions(authHeaders);
        if (!cancelled) setData(d);
      } catch {
        if (!cancelled) {
          setData((prev) => prev || { active: [], history: [] });
        }
      } finally {
        if (!cancelled) {
          timer = window.setTimeout(tick, 2000);
        }
      }
    }

    void tick();
    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [authHeaders, zdr]);

  const active = data?.active ?? [];
  const liveIds = active
    .map((s) => s.id)
    .sort()
    .join(",");
  const [found, setFound] = React.useState<Map<string, ActivitySession>>(
    () => new Map(),
  );
  React.useEffect(() => {
    if (!liveIds) return;
    let live = true;
    const load = () => {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      getActivitySessions(authHeaders, ME_INSIGHTS_BASE, {
        start: isoDay(yesterday),
        end: isoDay(today),
      }).then(
        (rows) => live && setFound(new Map(rows.map((r) => [r.id, r]))),
        () => {},
      );
    };
    load();
    const timer = window.setInterval(load, 20000);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, [authHeaders, liveIds]);

  if (zdr) {
    return (
      <Card title="Live now">
        <EmptyState compact icon="lock" title="Sessions aren't tracked">
          Zero data retention is on. Your workspace and conversation content are
          never stored.
        </EmptyState>
      </Card>
    );
  }

  if (!data) return null;
  const now = Date.now();

  return (
    <Card
      flush
      title={
        <>
          Live now
          {active.length > 0 && (
            <Pill tone="ok" icon="pulse">
              {active.length} live
            </Pill>
          )}
        </>
      }
      info="Agents running through dense right now. Finished sessions are in the table below."
    >
      {active.length === 0 ? (
        <EmptyState compact title="No live sessions">
          Start your agent with <code>dense</code> in front, such as{" "}
          <code>dense claude</code>, and it shows up here while it runs.
        </EmptyState>
      ) : (
        <Table label="Live sessions">
          <thead>
            <tr>
              <th>Repository</th>
              <th className="num">Spend</th>
              <th>Started</th>
              <th className="num">Requests</th>
            </tr>
          </thead>
          <tbody>
            {active.map((s) => {
              const match = found.get(s.id);
              const name = liveProject(s, match);
              const requests = match?.requests ?? s.request_count;
              const cells = (
                <>
                  <td>
                    <span className="strong" title={s.cwd}>
                      {name}
                    </span>
                    <span className="ds-cell-sub">
                      {[match?.branch ?? s.branch ?? "No branch", s.host]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </td>
                  <td className="num strong">
                    {match ? usdShort(match.cost_usd) : "—"}
                  </td>
                  <td>
                    {timeOf(s.started_at)}
                    <span className="ds-cell-sub">
                      {elapsed(s.started_at, now)}
                    </span>
                  </td>
                  <td className="num">
                    {requests == null ? "—" : humanize(requests)}
                  </td>
                </>
              );
              return match ? (
                <RowButton
                  key={s.id}
                  label={`Open the timeline of ${name}`}
                  onOpen={() => setOpen(match)}
                >
                  {cells}
                </RowButton>
              ) : (
                <tr key={s.id}>{cells}</tr>
              );
            })}
          </tbody>
        </Table>
      )}
      {open && (
        <SessionTimeline
          authHeaders={authHeaders}
          base={ME_INSIGHTS_BASE}
          session={open}
          onClose={() => setOpen(null)}
        />
      )}
    </Card>
  );
}
